import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  MessageSquare, Plus, Edit, Trash2, Eye, EyeOff, Search, RefreshCcw,
  HelpCircle, Users, Star, FileText, Settings, ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InterviewHubManagement = () => {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [stats, setStats] = useState({ total: 0, categories: 0, answered: 0 });
  const [formData, setFormData] = useState({
    question_tr: '',
    question_en: '',
    answer_tips_tr: '',
    answer_tips_en: '',
    category: 'general',
    difficulty: 'medium',
    is_active: true
  });

  useEffect(() => {
    loadQuestions();
    loadCategories();
    loadStats();
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/interview-questions`, { withCredentials: true });
      setQuestions(response.data.questions || []);
    } catch (error) {
      // Demo data
      setQuestions([
        { id: '1', question_tr: 'Kendinizi tanıtır mısınız?', question_en: 'Can you introduce yourself?', category: 'general', difficulty: 'easy', answer_count: 156, is_active: true },
        { id: '2', question_tr: 'Neden kabin memuru olmak istiyorsunuz?', question_en: 'Why do you want to be a cabin crew?', category: 'motivation', difficulty: 'medium', answer_count: 142, is_active: true },
        { id: '3', question_tr: 'Zor bir yolcu ile nasıl başa çıkarsınız?', question_en: 'How would you handle a difficult passenger?', category: 'behavioral', difficulty: 'hard', answer_count: 98, is_active: true },
        { id: '4', question_tr: 'Takım çalışması deneyiminizi anlatın', question_en: 'Tell us about your teamwork experience', category: 'behavioral', difficulty: 'medium', answer_count: 87, is_active: false }
      ]);
    }
    setLoading(false);
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/interview-categories`, { withCredentials: true });
      setCategories(response.data.categories || []);
    } catch (error) {
      setCategories([
        { id: 'general', name: 'Genel', count: 15 },
        { id: 'motivation', name: 'Motivasyon', count: 12 },
        { id: 'behavioral', name: 'Davranışsal', count: 18 },
        { id: 'situational', name: 'Durumsal', count: 10 },
        { id: 'english', name: 'İngilizce', count: 20 }
      ]);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/interview-questions/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      setStats({ total: 75, categories: 5, answered: 483 });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingQuestion) {
        await axios.put(`${API}/admin/interview-questions/${editingQuestion.id}`, formData, { withCredentials: true });
        toast.success('Soru güncellendi');
      } else {
        await axios.post(`${API}/admin/interview-questions`, formData, { withCredentials: true });
        toast.success('Soru eklendi');
      }
      setShowModal(false);
      resetForm();
      loadQuestions();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu soruyu silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/interview-questions/${id}`, { withCredentials: true });
      toast.success('Soru silindi');
      loadQuestions();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/interview-questions/${id}/toggle`, {}, { withCredentials: true });
      toast.success(currentStatus ? 'Pasif yapıldı' : 'Aktif yapıldı');
      loadQuestions();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const resetForm = () => {
    setFormData({
      question_tr: '',
      question_en: '',
      answer_tips_tr: '',
      answer_tips_en: '',
      category: 'general',
      difficulty: 'medium',
      is_active: true
    });
    setEditingQuestion(null);
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      question_tr: question.question_tr || '',
      question_en: question.question_en || '',
      answer_tips_tr: question.answer_tips_tr || '',
      answer_tips_en: question.answer_tips_en || '',
      category: question.category || 'general',
      difficulty: question.difficulty || 'medium',
      is_active: question.is_active
    });
    setShowModal(true);
  };

  const getCategoryBadge = (category) => {
    const badges = {
      'general': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Genel' },
      'motivation': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Motivasyon' },
      'behavioral': { bg: 'bg-green-100', text: 'text-green-700', label: 'Davranışsal' },
      'situational': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Durumsal' },
      'english': { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'İngilizce' }
    };
    return badges[category] || badges['general'];
  };

  const getDifficultyBadge = (difficulty) => {
    const badges = {
      'easy': { bg: 'bg-green-100', text: 'text-green-700', label: 'Kolay' },
      'medium': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Orta' },
      'hard': { bg: 'bg-red-100', text: 'text-red-700', label: 'Zor' }
    };
    return badges[difficulty] || badges['medium'];
  };

  const filteredQuestions = questions.filter(q => {
    const matchesSearch = q.question_tr?.toLowerCase().includes(search.toLowerCase()) ||
                          q.question_en?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || q.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-600 rounded-lg text-white">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Mülakat Soruları Yönetimi</h2>
            <p className="text-sm text-gray-500">{stats.total} soru, {stats.categories} kategori</p>
          </div>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="gap-2 bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4" />
          Yeni Soru
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow bg-gradient-to-br from-indigo-50 to-indigo-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <HelpCircle className="w-8 h-8 text-indigo-600" />
              <div>
                <p className="text-2xl font-bold text-indigo-700">{stats.total}</p>
                <p className="text-xs text-indigo-600">Toplam Soru</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-purple-700">{stats.categories}</p>
                <p className="text-xs text-purple-600">Kategori</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.answered}</p>
                <p className="text-xs text-green-600">Toplam Cevap</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Star className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-700">
                  {questions.filter(q => q.is_active).length}
                </p>
                <p className="text-xs text-amber-600">Aktif Soru</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Categories */}
      <Card className="border-0 shadow">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory('all')}
            >
              Tümü
            </Button>
            {categories.map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
              >
                {cat.name} ({cat.count})
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card className="border-0 shadow">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Soru ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={loadQuestions}>
              <RefreshCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Questions List */}
      <Card className="border-0 shadow">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCcw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <HelpCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Soru bulunamadı</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredQuestions.map((question) => {
                const catBadge = getCategoryBadge(question.category);
                const diffBadge = getDifficultyBadge(question.difficulty);
                return (
                  <div key={question.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${catBadge.bg} ${catBadge.text}`}>
                            {catBadge.label}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${diffBadge.bg} ${diffBadge.text}`}>
                            {diffBadge.label}
                          </span>
                          {!question.is_active && (
                            <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Pasif</span>
                          )}
                        </div>
                        <h3 className={`font-medium mb-1 ${question.is_active ? 'text-gray-800' : 'text-gray-400'}`}>
                          🇹🇷 {question.question_tr}
                        </h3>
                        <p className="text-sm text-gray-500 mb-2">
                          🇬🇧 {question.question_en}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-400">
                          <span>{question.answer_count || 0} cevap</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(question.id, question.is_active)}>
                          {question.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(question)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(question.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editingQuestion ? 'Soruyu Düzenle' : 'Yeni Mülakat Sorusu'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Soru (Türkçe) *</label>
                  <textarea
                    required
                    value={formData.question_tr}
                    onChange={(e) => setFormData({...formData, question_tr: e.target.value})}
                    className="w-full p-2 border rounded-md resize-none h-20"
                    placeholder="Türkçe soru..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Soru (İngilizce)</label>
                  <textarea
                    value={formData.question_en}
                    onChange={(e) => setFormData({...formData, question_en: e.target.value})}
                    className="w-full p-2 border rounded-md resize-none h-20"
                    placeholder="English question..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Kategori</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full h-10 px-3 border rounded-md"
                    >
                      <option value="general">Genel</option>
                      <option value="motivation">Motivasyon</option>
                      <option value="behavioral">Davranışsal</option>
                      <option value="situational">Durumsal</option>
                      <option value="english">İngilizce</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Zorluk</label>
                    <select
                      value={formData.difficulty}
                      onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                      className="w-full h-10 px-3 border rounded-md"
                    >
                      <option value="easy">Kolay</option>
                      <option value="medium">Orta</option>
                      <option value="hard">Zor</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Cevap İpuçları (Türkçe)</label>
                  <textarea
                    value={formData.answer_tips_tr}
                    onChange={(e) => setFormData({...formData, answer_tips_tr: e.target.value})}
                    className="w-full p-2 border rounded-md resize-none h-20"
                    placeholder="Cevap ipuçları..."
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
                    İptal
                  </Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                    {editingQuestion ? 'Güncelle' : 'Ekle'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default InterviewHubManagement;
